import { groupBy, mapValues, sortBy } from "lodash-es";
import { v4 as uuid } from "uuid";

import type { Color } from "@/model/baseTypes";
import type { FieldChange, StickyChange, StickyChangeKind } from "@/model/change";
import { stickyActivityKeys } from "@/model/change";
import { useCardStore } from "@/store/card";
import { useStickyTypeStore } from "@/store/stickyType";
import { useGlobalI18n } from "@/translations/i18n";
import { atDayStart } from "@/utils/date";

const { t } = useGlobalI18n();

// StickyChange with properties related to how it should be rendered
export interface DisplayableStickyChange extends StickyChange {
  id: string;
  expandable: boolean;
}

interface StickyChangesByDay {
  date: Date;
  changes: DisplayableStickyChange[];
}

export interface StickyChangesBySticky {
  id: string;
  changes: StickyChangesByKind;
}

type StickyChangesByKind = Record<StickyChangeKind, StickyChange[]>;

export function groupByDay(changes: StickyChange[]): StickyChangesByDay[] {
  return toByDayList(groupByDayStart(changes));
}

export function groupBySticky(changes: StickyChange[]): StickyChangesBySticky[] {
  return toByStickyList(mapValues(groupByStickyId(sortFields(changes)), groupByKind));
}

function toByStickyList(obj: Record<string, StickyChangesByKind>): StickyChangesBySticky[] {
  return Object.entries(obj).map(([id, changes]) => ({ id, changes }));
}

function toByDayList(obj: Record<string, StickyChange[]>): StickyChangesByDay[] {
  return Object.entries(obj).map(([date, changes]) => ({
    date: new Date(+date),
    changes: changes.map(makeDisplayable),
  }));
}

function sortFields(changes: StickyChange[]): StickyChange[] {
  return changes.map((change) => ({ ...change, fields: sortFieldChanges(change.fields) }));
}

function sortFieldChanges(fields: FieldChange[]): FieldChange[] {
  return sortBy(fields, (field) => Object.values(stickyActivityKeys).indexOf(field.name));
}

function groupByDayStart(changes: StickyChange[]) {
  return groupBy(changes, (change) => atDayStart(change.timestamp).getTime());
}

function groupByStickyId(changes: StickyChange[]) {
  return groupBy(changes, (change) => change.stickyId);
}

function groupByKind(changes: StickyChange[]) {
  return groupBy(changes, (change) => change.kind) as StickyChangesByKind;
}

function makeDisplayable(change: StickyChange) {
  return { ...change, id: uuid(), expandable: isExpandable(change) };
}

function isExpandable(change: StickyChange) {
  return ["update", "mirror", "unmirror", "link", "unlink"].includes(change.kind);
}

/**
 * Indicates if the change is clickable-but-not-expandable (specifically "create" events, if the card still exists)
 * This is used to allow users to click on a 'create' event to highlight the related card.
 * The other change types are already expandable, so they don't need to be explicitly clickable.
 * The 'delete' events are not clickable, since there is no card to highlight.
 */
export function isClickable(change: StickyChange) {
  if (change.kind !== "create") return false;

  // Only allow clicking if the card still exists (otherwise, there's nothing to highlight)
  return !!useCardStore().cards[change.stickyId];
}

function deletedCardField(change: StickyChangesBySticky, name: "text" | "type") {
  const fields = change.changes.delete?.[0].fields || [];
  return fields.find((field) => field.name === name)?.old as string;
}

/**
 * Extract the text and color from the card that the (compare) change belongs to
 * (even if the sticky was deleted)
 */
export function cardFromStickyChange(change: StickyChangesBySticky): {
  text: string;
  color: Color;
} {
  // Use the card if it exists
  const card = useCardStore().cards[change.id];
  if (card) {
    return { text: card.text, color: card.type.color };
  }

  // If the card was deleted, use the data from the deletion event
  const cardName = deletedCardField(change, "text") || "";
  return {
    text: t("boardDiff.deletedCardText", { cardName }).trim(), // 'cardName (deleted)'
    color: useStickyTypeStore().findStickyType(deletedCardField(change, "type"))?.color || [
      0.5, 0.5, 0.5, 1,
    ],
  };
}

// Cache of deleted cards, to show the correct text/type for updates that took place
// before the card was deleted. Only the deletion event can be guaranteed to contain
// the card's text & type, but since we load changes in reverse order, we can assume
// that card's deletion will be the first change we load, so we can cache the values.
// (Note: this still doesn't handle the case where the card was deleted after the timeframe,
// but there's no nice way to handle that, so we'll just label that card (deleted))
const deletedCardCache: Record<string, { cardName: string; typeName: string }> = {};

/**
 * Extract the text and color from the card that the (log) change belongs to
 * (even if the sticky was deleted)
 */
export function cardFromLogChange(change: DisplayableStickyChange): { text: string; color: Color } {
  const card = useCardStore().cards[change.stickyId];

  // If card still exists, extract needed values from that
  if (card) {
    return { text: card.text, color: card.type.color };
  }

  const cardName =
    (change.fields.find((field) => field.name === "text")?.old as string) || // If card was deleted in this change, fetch the text/type from the change itself
    deletedCardCache[change.stickyId]?.cardName || // if this was an update to a later-deleted sticky, try to find the cached value
    "";
  const text = t("boardDiff.deletedCardText", { cardName }).trim(); // 'cardName (deleted)'

  const typeName =
    (change.fields.find((field) => field.name === "type")?.old as string) || // same here, try to fetch the type from the change itself
    deletedCardCache[change.stickyId]?.typeName; // or from the cache -> if not, undefined (which makes the color grey)

  if (change.kind === "delete") {
    deletedCardCache[change.stickyId] = { cardName, typeName }; // Update the cache
  }

  return {
    text,
    color: useStickyTypeStore().findStickyType(typeName)?.color || [0.5, 0.5, 0.5, 1],
  };
}
